import { Alert, Paper, Typography } from "@mui/material";
import { capitalize } from "common/utils/strings";
import { FormikDimensionedNumberInput } from "common/components/FormikDimensionedNumberInput";
import { ImplementBoundaryViewModel } from "./boundaryModel";
import { NumberInputWithUsageNote } from "portal/components/NumberInputWithUsageNote";
import { TypedPathTo, useFormikValuesAtBasePath } from "portal/utils/typedPath";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import ImplementDiagramDark from "portal/images/implement-diagram-dark.svg?react";
import ImplementDiagramLight from "portal/images/implement-diagram-light.svg?react";
import React from "react";

interface ImplementDimensionsViewModel {
  workingWidthM: number;
  workingStartOffsetM: number;
  workingEndOffsetM: number;
  boundaryViewModel: ImplementBoundaryViewModel;
}

interface ImplementDimensionsSubformProps {
  basePath: TypedPathTo<ImplementDimensionsViewModel>;
  mode: "edit" | "readonly";
}

export const ImplementDimensionsSubform = ({
  basePath,
  mode,
}: ImplementDimensionsSubformProps): JSX.Element => {
  const { t } = useTranslation();
  const { measurementSystem } = useSelf();
  const values = useFormikValuesAtBasePath(basePath);

  // leave the boundary model alone if it's a point-capture boundary model
  const boundaryModelEditable =
    values.boundaryViewModel.measuringTapeV1 !== undefined;
  // leave the overall width alone if it's asymmetrical
  const overallWidthAsymmetrical =
    values.boundaryViewModel.measuringTapeV1?.overallWidthAsymmetrical;

  return (
    <Paper className="grid grid-cols-[min-content_minmax(0,1fr)] gap-4 p-4">
      <Typography className="col-span-full pb-4 font-bold">
        {capitalize(
          t("views.autotractor.equipment.tractors.physicalDimensions")
        )}
      </Typography>
      {(!boundaryModelEditable || overallWidthAsymmetrical) && (
        <Alert severity="warning" className="col-span-full">
          {t("views.autotractor.equipment.companionLockout")}
        </Alert>
      )}
      {/* two different implement diagrams for the two color schemes */}
      <ImplementDiagramDark className="hidden dark:block pl-4 w-[24rem] h-[27rem]" />
      <ImplementDiagramLight className="block dark:hidden pl-4 w-[24rem] h-[27rem]" />
      {/* the actual form */}
      <div className="flex flex-col gap-2 pl-8 justify-center">
        <NumberInputWithUsageNote
          numberInputProps={{
            name: basePath.workingStartOffsetM.$path,
            // FIXME cqb: I don't like doing string concatenation here, but
            // juxtaposing a number callout with the baked-in label would require
            // refactoring everything down to NumberInput to take element-valued
            // labels instead of strings
            label: `\u2460 ${capitalize(
              t("models.equipment.implement.startOffset")
            )}`,
            tProps: { minimum: 0 },
            dimensionedNumberInputProps: {
              minStoredValue: 0,
              storedUnit: "m",
              localSystem: measurementSystem,
              displayUnits: {
                imperial: "in",
                metric: "cm",
              },
              disabled: mode === "readonly" || !boundaryModelEditable,
            },
          }}
          tooltipProps={{
            severity: "info",
            icon: "\u2460",
            children: (
              <>
                {t(
                  "views.autotractor.equipment.implements.dimensionTooltips.startOffset"
                )}
              </>
            ),
          }}
        />
        <NumberInputWithUsageNote
          numberInputProps={{
            name: basePath.workingEndOffsetM.$path,
            label: `\u2461 ${capitalize(
              t("models.equipment.implement.endOffset")
            )}`,
            tProps: { minimum: 0 },
            dimensionedNumberInputProps: {
              minStoredValue: 0,
              storedUnit: "m",
              localSystem: measurementSystem,
              displayUnits: {
                imperial: "in",
                metric: "cm",
              },
              disabled: mode === "readonly" || !boundaryModelEditable,
            },
          }}
          tooltipProps={{
            severity: "info",
            icon: "\u2461",
            children: (
              <>
                {t(
                  "views.autotractor.equipment.implements.dimensionTooltips.endOffset"
                )}
              </>
            ),
          }}
        />
        <FormikDimensionedNumberInput
          name={basePath.boundaryViewModel.measuringTapeV1.overallLengthM.$path}
          label={`\u2462 ${capitalize(
            t("models.equipment.implement.overallLength")
          )}`}
          tProps={{ minimum: 0 }}
          dimensionedNumberInputProps={{
            minStoredValue: 0,
            storedUnit: "m",
            localSystem: measurementSystem,
            displayUnits: {
              imperial: "in",
              metric: "cm",
            },
            disabled: mode === "readonly" || !boundaryModelEditable,
          }}
          className=""
        />
        <NumberInputWithUsageNote
          numberInputProps={{
            name: basePath.workingWidthM.$path,
            label: `\u2463 ${capitalize(
              t("models.equipment.implement.workingWidth")
            )}`,
            tProps: { minimum: 0 },
            dimensionedNumberInputProps: {
              minStoredValue: 0,
              storedUnit: "m",
              localSystem: measurementSystem,
              displayUnits: {
                imperial: "in",
                metric: "cm",
              },
              disabled: mode === "readonly" || !boundaryModelEditable,
            },
          }}
          tooltipProps={{
            severity: "info",
            icon: "\u2463",
            children: (
              <>
                {t(
                  "views.autotractor.equipment.implements.dimensionTooltips.workingWidth"
                )}
              </>
            ),
          }}
        />
        <FormikDimensionedNumberInput
          name={basePath.boundaryViewModel.measuringTapeV1.overallWidthM.$path}
          label={`\u2464 ${capitalize(
            t("models.equipment.implement.overallWidth")
          )}`}
          tProps={{ minimum: 0 }}
          dimensionedNumberInputProps={{
            minStoredValue: 0,
            storedUnit: "m",
            localSystem: measurementSystem,
            displayUnits: {
              imperial: "in",
              metric: "cm",
            },
            disabled:
              mode === "readonly" ||
              !boundaryModelEditable ||
              overallWidthAsymmetrical,
          }}
          className=""
        />
      </div>
    </Paper>
  );
};
